/**
 * Turning an API error into text a user can read, in their language.
 *
 * The backend reports `{ code, message, params?, details? }` (see
 * `backend/src/errors/`). `code` is the contract: it resolves to
 * `errors.codes.<CODE>` in the locale files, and a backend test fails the build
 * if a catalogue code is missing from either language.
 *
 * The API's English `message` is deliberately never shown. It is a developer
 * aid and a debugging fallback; putting it in front of a Czech user is the
 * exact problem this module replaced, back when every catch block read
 * `err.response?.data?.message` first and only fell back to `t(...)`.
 */

import { categorizeError, getMessageKey } from './errorUtils';

export interface ApiErrorBody {
  code?: string;
  message?: string;
  params?: Record<string, string | number>;
  details?: { field: string; code: string }[];
}

/** Minimal shape of i18next's `t`, so callers can pass theirs without a cast. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

const bodyOf = (error: unknown): ApiErrorBody | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  return data && typeof data === 'object' ? (data as ApiErrorBody) : undefined;
};

/** A sentinel that cannot collide with real copy, so a missing key is detectable. */
const MISSING = ' missing';

const tryKey = (
  t: Translate,
  key: string,
  params?: Record<string, string | number>,
): string | undefined => {
  const value = t(key, { ...(params ?? {}), defaultValue: MISSING });
  return value === MISSING || value === key ? undefined : value;
};

/**
 * The one function a catch block should call.
 *
 * Resolution order, most specific first:
 *   1. `errors.codes.<CODE>` - the backend said exactly what went wrong
 *   2. `errors.<category>`   - network / auth / validation / notFound / server
 *   3. `fallbackKey`         - the caller's own wording for its operation
 *   4. `errors.unknown`
 */
export const translateError = (
  error: unknown,
  t: Translate,
  fallbackKey?: string,
): string => {
  const body = bodyOf(error);
  const category = categorizeError(error);

  if (body?.code) {
    const specific = tryKey(t, `errors.codes.${body.code}`, body.params);
    if (specific) return specific;
  }

  // A dropped connection has no body, and no per-operation wording helps.
  if (category === 'network') return t('errors.network');

  if (fallbackKey) {
    const fallback = tryKey(t, fallbackKey);
    if (fallback) return fallback;
  }

  return tryKey(t, getMessageKey(category)) ?? t('errors.unknown');
};

/**
 * Field-level failures from a VALIDATION_FAILED response, translated and keyed
 * by field name - feed straight into a form's `error` props.
 */
export const translateFieldErrors = (
  error: unknown,
  t: Translate,
): Record<string, string> => {
  const details = bodyOf(error)?.details;
  if (!Array.isArray(details)) return {};
  const out: Record<string, string> = {};
  for (const detail of details) {
    if (!detail?.field || !detail.code) continue;
    out[detail.field] = tryKey(t, `errors.codes.${detail.code}`) ?? t('errors.validation');
  }
  return out;
};

/** The API error code, when the response carried one. */
export const errorCode = (error: unknown): string | undefined => bodyOf(error)?.code;
